import { Link } from "react-router-dom";
import {
    Sparkles, Compass, Layers, ShieldCheck, Rocket, Users, ArrowUpRight, Target,
    LineChart, CheckCircle2, Award
} from "lucide-react";
import PageMeta from "../components/layout/PageMeta";
import PageHero from "../components/sections/PageHero";
import FinalCTA from "../components/sections/FinalCTA";

const values = [
    { icon: <Target className="w-5 h-5" />, title: "Ship production, not demos", copy: "We focus on deploying real, monitored systems that drive operational success, avoiding simple sandbox prototypes." },
    { icon: <Users className="w-5 h-5" />, title: "Senior ownership by default", copy: "Every squad is led by hands-on practitioners who directly own delivery and outcomes — minimizing management layers." },
    { icon: <Sparkles className="w-5 h-5" />, title: "AI where it improves work", copy: "We integrate artificial intelligence selectively, only where it genuinely automates tasks or guides business decisions." },
    { icon: <LineChart className="w-5 h-5" />, title: "Measured delivery, not vague effort", copy: "We track and prove progress through concrete, operational metrics and clear indicators of success." },
    { icon: <Compass className="w-5 h-5" />, title: "Domain context before automation", copy: "We respect your industry realities, regulations, and operational constraints first, then build solutions." },
    { icon: <ShieldCheck className="w-5 h-5" />, title: "Talent quality over resume volume", copy: "We deliver vetted specialists who match your technology stack, timezone, and project standards." },
];

const reasons = [
    { title: "AI-first delivery", copy: "We build systems where AI is the core operating layer, rather than an afterthought or a superficial wrapper." },
    { title: "Product + talent in one partner", copy: "Hire the senior squads to design and build your system, and the staffing pipeline to run and grow it over time." },
    { title: "Production-grade execution", copy: "Every line of code is shipped with observability, compliance reviews, structured testing, and service metrics." },
    { title: "Measurable ROI mapping", copy: "We align all discovery and implementation milestones with the business KPIs that define success for your teams." },
    { title: "Enterprise workflow understanding", copy: "We build with deep context around core business APIs, regulatory guidelines, and corporate security mandates." },
    { title: "Senior accountable squads", copy: "You communicate directly with the engineers, recruiters, and product leaders doing the actual work." },
];

const highlights = [
    { title: "Outcome-First Delivery", copy: "Success is measured by live processes and ROI, not code artifacts." },
    { title: "Senior Squads Default", copy: "Every engagement is staffed with hands-on senior practitioners." },
    { title: "Proven Accelerators", copy: "We configure configurable pre-built engines rather than beginning from blank pages." },
    { title: "Domain-Context Depth", copy: "We respects regulatory guidelines and operational constraints." },
];

export default function About() {
    return (
        <div className="pqx-page-enter">
            <PageMeta
                title="About Perceptiqx"
                description="Perceptiqx is an AI-first technology partner — combining AI engineering, product engineering and specialised staffing for modern enterprises."
            />

            <PageHero
                eyebrow="About Perceptiqx"
                title="The AI-first partner enterprises trust"
                titleAccent="to ship — not to pilot."
                description="Perceptiqx exists at the intersection of AI, product engineering and specialised talent — the three forces that decide whether digital programs ship or stall."
                primaryCta={{ to: "/contact", label: "Start a conversation" }}
                secondaryCta={{ to: "/ai-solutions", label: "Explore services" }}
            />

            {/* Who we are */}
            <section className="pqx-section bg-[#FAFAF8]" data-testid="about-who">
                <div className="pqx-container">
                    <div className="grid lg:grid-cols-[1fr_1.3fr] gap-14">
                        <div>
                            <span className="pqx-eyebrow">Who we are</span>
                            <h2 className="pqx-display mt-5 text-3xl md:text-5xl text-[#303030]">
                                Engineers, designers, and recruiters building one enterprise AI practice.
                            </h2>
                        </div>
                        <div className="space-y-6 text-[#303030]/80 text-[16px] leading-relaxed">
                            <p>
                                PerceptIQX is an AI-first services, staffing, consulting, and product engineering company. We help businesses build intelligent AI products, automate workflows, and scale specialized technology teams.
                            </p>
                            <p>
                                Our teams bring a rare combination of disciplines — applied AI,
                                modern engineering, product design and specialised talent
                                acquisition — and operate as senior, accountable pods embedded
                                with our clients.
                            </p>
                            <p>
                                We work with enterprises and high-growth product companies that
                                want AI delivered with the rigour their business demands.
                            </p>
                        </div>
                    </div>

                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {highlights.map((h, i) => (
                            <div
                                key={h.title}
                                className="rounded-2xl bg-white border border-[#303030]/8 p-6"
                            >
                                <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#FF4040]/10 text-[#FF4040] mb-4">
                                    <CheckCircle2 className="w-5 h-5" />
                                </span>
                                <p className="pqx-display text-lg text-[#303030] font-bold">{h.title}</p>
                                <p className="mt-2 text-xs text-[#303030]/65 leading-relaxed">
                                    {h.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* What we do + how we work */}
            <section className="pqx-section bg-white" data-testid="about-what">
                <div className="pqx-container grid lg:grid-cols-2 gap-6 md:gap-10">
                    <div className="pqx-card-elevated !p-8 md:!p-10">
                        <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#FF4040]/12 text-[#FF4040]">
                            <Sparkles className="w-5 h-5" />
                        </span>
                        <h3 className="pqx-display text-2xl md:text-3xl text-[#303030] mt-6">
                            What we do
                        </h3>
                        <p className="mt-4 text-[#303030]/75 leading-relaxed">
                            We design and deliver AI solutions, productised AI accelerators,
                            enterprise-grade software and specialised technology talent. Our
                            engagements range from focused AI prototypes to multi-year programs
                            that combine product engineering, MLOps and managed staffing.
                        </p>
                        <ul className="mt-6 space-y-2.5">
                            {["AI services and copilots", "Product accelerators", "Modern engineering teams", "Specialised staffing"].map((i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-[#303030]/80">
                                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#FF4040] shrink-0" />
                                    {i}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="pqx-card-elevated !p-8 md:!p-10">
                        <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#303030] text-white">
                            <Compass className="w-5 h-5" />
                        </span>
                        <h3 className="pqx-display text-2xl md:text-3xl text-[#303030] mt-6">
                            How we work
                        </h3>
                        <p className="mt-4 text-[#303030]/75 leading-relaxed">
                            Senior-led pods. Short feedback loops. Production-grade defaults. We
                            structure every engagement around a measurable outcome and
                            instrument the work so progress is visible to everyone involved.
                        </p>
                        <ul className="mt-6 space-y-2.5">
                            {["Outcome-mapped roadmaps", "Two-week delivery cycles", "Shared dashboards and metrics", "Knowledge transfer baked in"].map((i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-[#303030]/80">
                                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#FF4040] shrink-0" />
                                    {i}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Values / Principles */}
            <section
                className="pqx-section bg-[#191919] text-[#FAFAF8] relative overflow-hidden"
                data-testid="about-values"
            >
                <div className="absolute inset-0 pqx-grid-bg-dark opacity-40" />
                <div className="pqx-container relative z-10">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow !text-[#FF4040]">Our principles</span>
                        <h2 className="pqx-display mt-5 text-3xl md:text-5xl">
                            The principles we hire, build, and ship by.
                        </h2>
                    </div>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {values.map((v) => (
                            <div
                                key={v.title}
                                className="pqx-glass-dark rounded-2xl p-6"
                            >
                                <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#FF4040]/15 text-[#FF4040]">
                                    {v.icon}
                                </span>
                                <h3 className="pqx-display text-lg mt-4">{v.title}</h3>
                                <p className="mt-2 text-sm text-white/65 leading-relaxed">
                                    {v.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why clients choose us */}
            <section className="pqx-section bg-[#FAFAF8]" data-testid="about-why">
                <div className="pqx-container">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow">Why clients choose us</span>
                        <h2 className="pqx-display mt-5 text-3xl md:text-5xl text-[#303030]">
                            Six reasons enterprise leaders pick Perceptiqx.
                        </h2>
                    </div>
                    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {reasons.map((r, i) => (
                            <div
                                key={r.title}
                                data-testid={`about-reason-${i}`}
                                className="pqx-card-elevated !p-6 bg-white border border-[#303030]/5 rounded-2xl"
                            >
                                <span className="font-mono text-[11px] text-[#303030]/45 uppercase tracking-[0.18em]">
                                    Reason 0{i + 1}
                                </span>
                                <h3 className="pqx-display text-lg text-[#303030] mt-4">
                                    {r.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#303030]/70 leading-relaxed">
                                    {r.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <FinalCTA
                title="Start with a conversation."
                description="Bring us a roadmap, a problem statement or a blank page. We'll help you decide if AI is the right answer — and what shape it should take."
                primary={{ to: "/contact", label: "Start a conversation" }}
                secondary={{ to: "/ai-solutions", label: "Explore services" }}
            />
        </div>
    );
}
